/**
 * chatPhoto — pick a photo from the gallery and shrink it for chat.
 *
 * Mirrors AvatarUploadController's picker split (Android system photo picker
 * via FilePicker so we don't need READ_MEDIA_IMAGES; Capacitor Camera /
 * PHPicker / file input everywhere else) and its lazy HEIC→JPEG transcode.
 * Instead of a crop UI, chat photos get a straight canvas downscale:
 * longest edge capped at MAX_EDGE px, JPEG at JPEG_QUALITY.
 */

import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import { FilePicker } from '@capawesome/capacitor-file-picker';
import { isAndroidNative } from '../../../util/platform';

/** Longest output edge. 1280 reads crisp on phones and lands ~150-400KB. */
const MAX_EDGE = 1280;
const JPEG_QUALITY = 0.8;
/** Reject absurd source files before we even try to decode them (base64 is
 *  ~4/3 the raw size, so this string-length gate ≈ a 10MB raw file). */
const MAX_RAW_DATAURL_LENGTH = 14 * 1024 * 1024;

export class ChatPhotoTooLargeError extends Error {
  constructor() {
    super('Photo is too large. Pick one under 10MB.');
    this.name = 'ChatPhotoTooLargeError';
  }
}

/**
 * Open the photo picker and return a chat-ready JPEG data URL,
 * or null if the player cancelled.
 * @throws ChatPhotoTooLargeError when the source file is over the raw cap.
 */
export async function pickChatPhoto(): Promise<string | null> {
  let dataUrl: string;
  let sourceName: string | undefined;
  try {
    if (isAndroidNative()) {
      const res = await FilePicker.pickImages({ readData: true });
      const file = res.files?.[0];
      if (!file?.data) return null;
      sourceName = file.name;
      dataUrl = `data:${file.mimeType || 'image/jpeg'};base64,${file.data}`;
    } else {
      const photo = await Camera.getPhoto({
        quality: 90,
        resultType: CameraResultType.Base64,
        source: CameraSource.Photos,
        width: MAX_EDGE,
        height: MAX_EDGE,
      });
      if (!photo.base64String) return null;
      const format = photo.format || 'jpeg';
      dataUrl = `data:image/${format};base64,${photo.base64String}`;
    }
  } catch {
    // Picker cancelled or permission denied — treat as a cancel.
    return null;
  }

  if (dataUrl.length > MAX_RAW_DATAURL_LENGTH) {
    throw new ChatPhotoTooLargeError();
  }

  dataUrl = await toRenderableJpeg(dataUrl, sourceName);
  return downscaleToJpeg(dataUrl);
}

/** HEIC/HEIF → JPEG so the Android WebView can decode it (same landmine as
 *  avatars: Samsung shoots HEIF and FilePicker hands back the raw file). */
async function toRenderableJpeg(dataUrl: string, name?: string): Promise<string> {
  const isHeic =
    /^data:image\/(heic|heif)/i.test(dataUrl) || /\.(heic|heif)$/i.test(name ?? '');
  if (!isHeic) return dataUrl;

  const blob = await (await fetch(dataUrl)).blob();
  const { default: heic2any } = await import('heic2any');
  const out = await heic2any({ blob, toType: 'image/jpeg', quality: 0.9 });
  const jpeg = Array.isArray(out) ? out[0] : out;
  return await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error ?? new Error('read failed'));
    reader.readAsDataURL(jpeg);
  });
}

/** Decode → scale down so the longest edge is MAX_EDGE → JPEG data URL. */
async function downscaleToJpeg(dataUrl: string): Promise<string> {
  const img = await new Promise<HTMLImageElement>((resolve, reject) => {
    const el = new Image();
    el.onload = () => resolve(el);
    el.onerror = () => reject(new Error('Image decode failed'));
    el.src = dataUrl;
  });

  const scale = Math.min(1, MAX_EDGE / Math.max(img.naturalWidth, img.naturalHeight));
  const w = Math.max(1, Math.round(img.naturalWidth * scale));
  const h = Math.max(1, Math.round(img.naturalHeight * scale));

  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas unavailable');
  ctx.drawImage(img, 0, 0, w, h);
  return canvas.toDataURL('image/jpeg', JPEG_QUALITY);
}
